import { Button } from '@design/components/button/Button';
import { ButtonLink } from '@design/components/button-link/ButtonLink';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useChatSettings } from '@terminal/views/alfred/research/use-chat-settings/useChatSettings';
import { MarkdownEditor } from '@terminal/widgets/markdown-editor/MarkdownEditor';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './PersonalizationForm.module.scss';

interface PersonalizationFormProps {
  onCancel?: () => void;
  showCancelButton?: boolean;
  onSaveSuccess?: () => void;
}

export const PersonalizationForm = ({
  onCancel,
  showCancelButton = true,
  onSaveSuccess,
}: PersonalizationFormProps) => {
  const { t } = useTranslation('chat');
  const { trackAlfredPersonalize } = usePosthogEvents();

  const { setPreferenceFormat, preferenceFormat } = useChatSettings(state => ({
    preferenceFormat: state.preferenceFormat,
    setPreferenceFormat: state.setPreferenceFormat,
  }));
  const [instructions, setInstructions] = useState(preferenceFormat || '');
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const isDirty = instructions !== (preferenceFormat || '');

  const handleSave = async () => {
    setIsLoading(true);
    setSaveError(false);
    const response = await setPreferenceFormat(instructions);

    if (!response.error) {
      trackAlfredPersonalize(POSTHOG_TRACKING_SOURCES.ALFRED);
      onSaveSuccess?.();
    } else {
      setSaveError(true);
    }

    setIsLoading(false);
  };

  const handleCancel = () => {
    setInstructions(preferenceFormat || '');
    onCancel?.();
  };

  const showExample = () => {
    setInstructions(
      state => state + '\n' + t('settings.exampleText', { joinArrays: '\n' })
    );
  };

  useEffect(() => {
    if (preferenceFormat) {
      setInstructions(preferenceFormat);
    }
  }, [preferenceFormat]);

  return (
    <div
      className={clsx(styles.formRoot, {
        [styles.formRootStandalone]: !showCancelButton,
      })}
    >
      <div
        className={clsx(styles.formFields, {
          [styles.formFieldsStandalone]: !showCancelButton,
        })}
      >
        <div className={styles.inputWrapper}>
          <div className={styles.labels}>
            <span>{t('settings.instructions')}</span>
            <ButtonLink
              label={t('settings.showExample')}
              onClick={showExample}
            />
          </div>
          <div className={styles.editorWrapper}>
            <MarkdownEditor
              value={instructions}
              onChange={setInstructions}
              placeholder={t('settings.placeholder')}
              className="personalization-editor"
            />
          </div>

          <div className={styles.proTip}>{t('settings.proTip')}</div>
        </div>
      </div>
      {saveError && (
        <p className={styles.saveError} role="alert">
          {t('settings.saveError')}
        </p>
      )}
      <div
        className={clsx(styles.footer, {
          [styles.footerStandalone]: !showCancelButton,
        })}
      >
        {showCancelButton && (
          <Button
            className={styles.cancelButton}
            variant="tertiary"
            size="large"
            label={t('settings.cancel')}
            onClick={handleCancel}
          />
        )}
        {isDirty && (
          <Button
            className={clsx(styles.saveButton, {
              [styles.saveButtonStandalone]: !showCancelButton,
            })}
            variant="primary"
            size="large"
            label={t('settings.save')}
            onClick={handleSave}
            disabled={isLoading}
            data-testid="save-button"
          />
        )}
      </div>
    </div>
  );
};
